import React, { useEffect, useState } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { SectionCard } from './SectionCard';
import { getPlatformColor, getPlatformName } from '../config/platforms';
import { formatMoney } from '../utils/money';
import { useAuth } from '../contexts/AuthContext';
import type { PlatformRow } from '../types/stats';

const n = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString());

/** One network's line from /api/analytics/ad-spend — where the money was delivered. */
interface Delivered {
  platform: string;
  spend: number;
  reach: number | null;
  likes: number | null;
}

/**
 * What the posts earned on their own against what the boosts bought, per network (2026-10-17).
 *
 * **Paid figures are by the network that delivered them**, read from the same endpoint as Ad
 * Spend By Network so the two cards agree. Grouping by the network a post was boosted from
 * (v1.34.0) put every Meta boost under Instagram — they were all Instagram posts — so the share
 * Meta delivered on Facebook vanished into the Instagram row and Facebook had no row at all.
 *
 * **Likes are the paid engagement**, because Meta's delivery split reports likes per network but
 * not comments or shares; organic likes sit beside them so the comparison is like for like.
 * **Reach** is compared only where both sides were measured: only Instagram reports organic
 * reach. **Impressions are not used**: Meta withdrew organic post impressions in v22.
 */
export const OrganicVsPaid: React.FC<{ byPlatform: PlatformRow[]; days: number }> = ({ byPlatform, days }) => {
  const { token } = useAuth();
  const [delivered, setDelivered] = useState<Delivered[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/analytics/ad-spend?days=${days}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => (res.ok ? res.json() : null))
      .then(body => { if (!cancelled) setDelivered(body?.networks || []); })
      .catch(() => { if (!cancelled) setDelivered([]); });
    return () => { cancelled = true; };
  }, [days, token]);

  const rows = (delivered || [])
    .filter(d => (d.spend || 0) > 0 || (d.likes || 0) > 0)
    .map(d => ({ paid: d, organic: byPlatform.find(p => p.platform === d.platform) }));

  return (
    <SectionCard
      id="organic-vs-paid"
      title="Organic Vs Paid"
      description="What posts earned on their own against what boosts added on each network, as delivered — the same figures as Ad Spend By Network."
    >
      {delivered === null ? (
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No boosts ran in this period.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Network</TableCell>
                <TableCell align="right">Organic Likes</TableCell>
                <TableCell align="right">Paid Likes</TableCell>
                <TableCell align="right">Organic Reach</TableCell>
                <TableCell align="right">Paid Reach</TableCell>
                <TableCell align="right">Spend</TableCell>
                <TableCell align="right">Per Paid Like</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ paid, organic }) => {
                const paidLikes = paid.likes || 0;
                const organicReach = organic?.reach ?? null;
                const bothReach = organicReach != null && paid.reach != null;
                return (
                  <TableRow key={paid.platform}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getPlatformColor(paid.platform) }} />
                        {getPlatformName(paid.platform)}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{n(organic?.likes ?? 0)}</TableCell>
                    <TableCell align="right">{n(paidLikes)}</TableCell>
                    <TableCell align="right">{bothReach ? n(organicReach) : '—'}</TableCell>
                    <TableCell align="right">
                      {n(paid.reach)}
                      {bothReach && (organicReach || 0) > 0 && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          ({Math.round((paid.reach || 0) / (organicReach || 1))}×)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{formatMoney(paid.spend || 0)}</TableCell>
                    <TableCell align="right">{paidLikes > 0 ? formatMoney((paid.spend || 0) / paidLikes) : '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        Organic counts posts published in this period; paid counts boosts that ran in it, on the network Meta
        actually delivered them — an Instagram boost also runs on Facebook. Paid engagement is likes only, as Meta
        reports no comments or shares per network. Organic reach is shown only where the network reports it
        (Instagram); impressions are left out because Meta no longer reports organic impressions.
      </Typography>
    </SectionCard>
  );
};
